import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Printer, Scissors } from "lucide-react";
import { num, shortDate } from "@/lib/format";
import { metersLabel, ordinal } from "@/lib/cutTickets";
import { TicketEditor } from "@/components/cuttickets/TicketEditor";
import { SeasonCutSummary } from "@/components/cuttickets/SeasonCutSummary";

const TicketCard = ({ t, onOpen }) => (
  <Card className="cursor-pointer space-y-2 p-4 transition-colors hover:border-primary/50" onClick={onOpen} data-testid={`ticket-card-${t.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-md px-2.5 py-0.5 font-mono-erp text-sm font-bold tracking-wider ${t.code ? "bg-primary text-primary-foreground" : "border border-dashed border-amber-400/60 text-amber-400"}`} data-testid={`ticket-card-code-${t.id}`}>{t.code ? t.code.toUpperCase() : "SENZA CODICE"}</span>
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">{ordinal(t.number)}</span>
        </div>
        <p className="mt-1.5 truncate font-display font-semibold">{t.sheet_name}</p>
        <p className="text-xs text-muted-foreground">{shortDate(t.created_at)}</p>
      </div>
      <Button size="icon" variant="ghost" className="h-7 w-7" title="Stampa" onClick={(e) => { e.stopPropagation(); window.open(`/modellistica/tagliato/${t.id}/stampa`, "_blank"); }} data-testid={`ticket-card-print-${t.id}`}><Printer className="h-3.5 w-3.5" /></Button>
    </div>
    <p className="text-xs text-muted-foreground">{t.rows.map((r) => r.color || "-").join(" · ") || "Nessun lotto"}</p>
    <p className="text-sm"><b>{num(t.pieces)}</b> capi · <b>{(t.markers || []).length}</b> grafici · <b>{metersLabel(t.meters)}</b></p>
    {t.discharged_at && <p className="text-[11px] text-emerald-400" data-testid={`ticket-card-discharged-${t.id}`}>Tessuto scaricato</p>}
  </Card>
);

export const CutTicketsSection = ({ season, sheets, search }) => {
  const [editing, setEditing] = useState(null);
  const [summary, setSummary] = useState(false);
  const { data: tickets = [], isLoading } = useQuery({ queryKey: ["cut-tickets", season], enabled: !!season, queryFn: async () => (await api.get("/cut-tickets", { params: { season } })).data });
  const w = search.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = tickets.filter((t) => w.every((x) => [t.sheet_name, t.code, ordinal(t.number), ...t.rows.map((r) => r.color)].join(" ").toLowerCase().includes(x)));
  return (
    <div data-testid="cut-tickets-section">
      <div className="mb-4 flex justify-end gap-2">
        <Button variant="outline" onClick={() => setSummary(!summary)} data-testid="cut-summary-toggle">{summary ? "Vedi le schede" : "Riepilogo stagione"}</Button>
        <Button onClick={() => setEditing({})} data-testid="new-ticket-btn"><Plus className="mr-2 h-4 w-4" />Nuova scheda taglio</Button>
      </div>
      {isLoading && <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}
      {!isLoading && shown.length === 0 && (
        <p className="flex flex-col items-center gap-2 py-16 text-center text-sm text-muted-foreground" data-testid="tickets-empty">
          <Scissors className="h-6 w-6" />{tickets.length ? "Nessuna scheda taglio corrisponde alla ricerca." : "Nessuna scheda taglio per questa stagione: creane una scegliendo l'articolo, i colori e le quantità da tagliare."}
        </p>
      )}
      {summary && !isLoading && shown.length > 0 && <SeasonCutSummary tickets={shown} />}
      {!summary && <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {shown.map((t) => <TicketCard key={t.id} t={t} onOpen={() => setEditing(t)} />)}
      </div>}
      {editing && <TicketEditor ticket={editing} onClose={() => setEditing(null)} />}
    </div>
  );
};
